/*
 * @Author: yidan.chen
 * @Date: 2025-02-10 15:29:02
 * @Last Modified by: yidan.chen
 * @Last Modified time: 2025-05-13 16:34:09
 */
import { commonServerInstance, downloadInstance } from '@/utilities/instances'

// limited authority
export const fetchPartners = async () => {
  const response = await commonServerInstance.get('/settle/partners')
  return response?.data?.data
}

// whole authority without limited
export const fetchPartnersWithoutLimited = async () => {
  const response = await commonServerInstance.get('/partners')
  return response?.data?.data
}

/**
 * A row of `GET /settle/customers` as it arrives on the wire —
 * `PartnerCustomerService::getCustomers`, a bare `select('id, alias as name')`.
 * Both alias spellings are optional because the endpoint has been observed
 * emitting either, and a blank alias arrives as `''` rather than null.
 */
interface ISettleCustomerRow {
  id: string | number
  alias?: string
  name?: string
}

/**
 * A customer row as callers get it: `name` is always a string. Normalising
 * here rather than at each picker is what keeps a row that arrives with only
 * `alias` from rendering as `991:undefined`.
 */
export interface ISettleCustomer {
  id: string | number
  name: string
}

export const fetchCustomers = async (params?: {
  partner_id: string
}): Promise<ISettleCustomer[]> => {
  const response = await commonServerInstance.get('/settle/customers', {
    params: params || {}
  })
  const rows: ISettleCustomerRow[] = response?.data?.data ?? []
  // The one and only place the two alias spellings are collapsed.
  return rows.map(row => ({ id: row.id, name: row.alias ?? row.name ?? '' }))
}

export const fetchBillingStatus = async () => {
  const response = await commonServerInstance.get('/settle/bill/status')
  return response?.data?.data
}

export const downloadFile: (
  response: any,
  filename?: string
) => Promise<any> = async (response: any, filename?: string) => {
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url

  const contentDisposition = response.headers['content-disposition']
  let finalFilename = filename

  /*
   * Two alternatives, quoted FIRST. The quoted branch has to win the race:
   * `([^;\s]+)` would otherwise match `"a.csv"` including its quotes, and the
   * previous single greedy pattern (`/filename="?(.+)"?/i`) swallowed the
   * closing quote into the capture, so every quoted header yielded a stray
   * trailing `"` in the saved filename.
   *
   * `??` rather than `||` on purpose: the alternative that did not match is
   * `undefined`, but an empty quoted name (`filename=""`) is a legitimate `''`
   * capture that must NOT fall through to the token group — it falls through to
   * the `download.csv` fallback below instead.
   *
   * RFC-5987's `filename*=UTF-8''…` is deliberately not parsed: `filename`
   * followed by `*` fails `filename\s*=`, so a header carrying both parameters
   * resolves to the plain one. No server in this app emits the extended form.
   */
  if (!finalFilename && contentDisposition) {
    const filenameMatch = contentDisposition.match(
      /filename\s*=\s*"([^"]*)"|filename\s*=\s*([^;\s]+)/i
    )
    if (filenameMatch) {
      finalFilename = filenameMatch[1] ?? filenameMatch[2]
    }
  }

  // Fallback filename if no filename is provided
  if (!finalFilename) {
    finalFilename = 'download.csv'
  }

  link.setAttribute('download', finalFilename)
  document.body.appendChild(link)
  link.click()

  // Clean up
  link.remove()
  window.URL.revokeObjectURL(url)

  return response
}

export const commonDownloadService = async (url: string, filename?: string) => {
  try {
    const response = await downloadInstance.get(url, {
      responseType: 'blob'
    })

    return await downloadFile(response, filename)
  } catch (error) {
    console.error('Download error:', error)
    throw error
  }
}

export const batchApprove = async (params: {
  model_ids: string
  model_type: string
  comment?: string
  [key: string]: any
}) => {
  const { model_ids, model_type, ...rest } = params
  const response = await commonServerInstance.post(
    `/api/v1/approval/process/model-bulk-approve`,
    {
      model_ids,
      model_type,
      strict_mode: true,
      ...(rest || {})
    }
  )
  return response?.data?.data
}

export const batchReject = async (params: {
  model_ids: string
  model_type: string
  comment?: string
  [key: string]: any
}) => {
  const { model_ids, model_type, ...rest } = params
  const response = await commonServerInstance.post(
    `/api/v1/approval/process/model-bulk-decline`,
    {
      model_ids,
      model_type,
      strict_mode: true,
      ...(rest || {})
    }
  )
  return response?.data?.data
}
